<template>

    <div v-if="!project.is_onboarded && !user.is_sub_account && !project.space.agency_id && hasChecklist" id="checklist" :class="{ expanded: this.expanded }">

        <div class="checklist-header" @click.prevent="toggle">
            <div class="title">
                <span><i class="fas fa-face-smile-hearts mr-5"></i>{{ $t('labels.getting_started') }}</span>
                <i v-if="!expanded" class="icon fas fa-chevron-circle-up"></i>
                <i v-else class="icon fas fa-chevron-circle-down"></i>
            </div>
            <div class="bars">
                <div class="bar" v-for="bar in bars"
                    :key="`bar-${bar.key}`"
                    :class="{ 'complete': bar.complete }">
                </div>
            </div>
        </div>

        <div v-if="expanded" class="checklist-body">
            <div
                v-for="task in tasks"
                :key="taskKey(task)"
                class="checklist-item"
                :class="{ active: selectedTask === taskKey(task), complete: hasTask(task.slug) }">
                <div class="checklist-item-header" @click.prevent="select(task)">
                    <div class="label">
                        <i v-if="!hasTask(task.slug)" class="icon fas fa-square-dashed"></i>
                        <i v-else class="icon fas fa-check-square text-success"></i>
                        <div class="title" :class="{ 'complete': hasTask(task.slug) }">{{ task.title }}</div>
                    </div>
                    <i v-if="task.desc && selectedTask === taskKey(task)" class="fas fa-chevron-up text-muted ml-10"></i>
                    <i v-else-if="task.desc" class="fas fa-chevron-down text-muted ml-10"></i>
                </div>
                <div v-if="task.desc && selectedTask === taskKey(task)" class="checklist-item-body">
                    <div class="desc" v-html="task.desc" />
                    <div class="actions">
                        <v-button v-for="a in task.actions" :key="a.label" size="sm" btn-block arrow @click.native="open(a)">
                            <span>{{ a.label }}</span>
                        </v-button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="expanded" class="checklist-footer">
            <a href="#" @click.prevent="hideChecklist"><i class="fas fa-close mr-5"></i>{{ $t('labels.hide') }}</a>
        </div>

    </div>

</template>

<style scoped lang="scss">

    #checklist {
        background: $color-white;
        border-radius: 10px;
        box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        box-sizing: border-box;
        bottom: 0;
        left: 0;
        margin: 20px;
        overflow: hidden;
        position: fixed;
        width: 400px;
        z-index: 501;
        @media (max-width: map-get($grid-breakpoints, 'sm')) {
            border-radius: 0;
            margin: 0;
            width: 100%;
        }
    }

    .checklist-header {
        background: $color-white;
        cursor: pointer;
        padding: 20px;
        transition: background-color .3s;
        &:hover {
            background: $color-bg;
        }
        .title {
            @include font-semibold(14px);
            align-items: center;
            color: $color-text;
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 100%;
        }
        .bars {
            display: grid;
            gap: 3px;
            grid-auto-flow: column;
            .bar {
                background: $color-text;
                border-radius: 2px;
                height: 2px;
                opacity: 0.5;
                width: 100%;
                &.complete {
                    background: $color-success;
                    opacity: 1;
                }
            }
        }
    }

    .checklist-body {
        border-top: 1px solid $color-rule;
        padding: 5px 0 10px 0;
    }

    .checklist-footer {
        @include font-regular(12px);
        padding: 10px 15px;
        a {
            color: $color-muted;
            text-decoration: none;
            transition: color .3s;
            &:hover {
                color: $color-text;
            }
        }
    }

    .checklist-item {
        border-bottom: 1px solid $color-rule-light;
        &:last-child {
            border: none;
        }
        &.active, &.active.complete {
            .checklist-item-header .title {
                color: $color-primary;
            }
            .checklist-item-header:hover .title {
                color: $color-primary;
            }
        }
        &.complete {
            .checklist-item-header .title {
                color: $color-text;
                text-decoration: line-through;
            }
            .checklist-item-header:hover .title {
                color: $color-primary;
            }
        }
    }

    .checklist-item-header {
        align-items: center;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        padding: 10px 20px;
        text-decoration: none;
        &:hover {
            .title {
                color: $color-primary;
            }
        }
        .label {
            align-items: center;
            display: flex;
        }
        .icon {
            color: $color-muted;
            font-size: 20px;
            margin-right: 10px;
            width: 20px;
        }
        .title {
            @include font-medium(13px);
            color: $color-text;
            transition: color .3s;
        }
    }

    .checklist-item-body {
        @include font-regular(14px);
        color: $color-text;
        line-height: 1.6em;
        padding: 0 20px 20px 50px;
        .actions {
            display: grid;
            gap: 5px;
            margin-top: 15px;
        }
    }

</style>

<script>

export default {

    data() {
        return {
            expanded: false,
            tasks: [
                {
                    slug: 'connected-google',
                    title: this.$t('labels.connect_google_business_profile'),
                    desc: this.$t('tasks.google'),
                    actions: [
                        { label: this.$t('labels.connect_google'), to: { name: 'settings.integrations.show', params: { slug: 'google-my-business' } } },
                    ],
                },
                {
                    slug: 'imported-customers',
                    title: this.$t('labels.import_customers'),
                    desc: this.$t('tasks.customers'),
                    actions: [
                        { label: this.$t('labels.import_customers'), to: { name: 'customers', query: { import: 'add' } } },
                    ],
                },
                {
                    slug: 'reviewed-strategy',
                    title: this.$t('labels.view_strategy'),
                    desc: this.$t('tasks.strategy'),
                    actions: [
                        { label: this.$t('labels.view_strategy'), to: { name: 'settings.strategy' } },
                    ],
                },
                {
                    slug: 'reviewed-templates',
                    title: this.$t('labels.view_email_templates'),
                    desc: this.$t('tasks.templates'),
                    actions: [
                        { label: this.$t('labels.view_email_templates'), to: { name: 'settings.email', query: { preview: '1' } } },
                    ],
                },
                {
                    key: 'customize-review-page',
                    slug: ['updated-form', 'created-link'],
                    title: this.$t('labels.customize_review_page'),
                    desc: this.$t('tasks.review_page'),
                    actions: [
                        { label: this.$t('labels.view_review_pages'), to: { name: 'forms' } },
                    ],
                },
                {
                    slug: 'activated-project',
                    title: this.$t('labels.automate') + ' 🎉',
                    desc: this.$t('tasks.automate'),
                    actions: [
                        { label: this.$t('labels.automate'), modal: 'project-activate' },
                    ],
                }
            ]
        }
    },

    computed: {

        hasChecklist() {
            return !this.$store.getters.checklists.includes(this.project.id);
        },

        selectedTask() {
            return this.$store.getters.task;
        },

        bars() {
            let tasks = [...this.tasks];
            return tasks
                .sort((a, b) => this.hasTask(b.slug) - this.hasTask(a.slug))
                .map(task => ({
                    key: this.taskKey(task),
                    complete: this.hasTask(task.slug),
                }));
        },

    },

    watch: {

        $route(val) {
            if (val.name !== 'dashboard') {
                this.expanded = false;
            }
        },

        selectedTask(val) {
            if (val) {
                this.expanded = true;
            }
        },

    },

    methods: {

        taskKey(task) {
            return task.key || task.slug;
        },

        toggle() {
            this.expanded = !this.expanded;
            this.$store.commit('setTask', null);
        },

        select(task) {
            if (!task.desc) return;
            const key = this.taskKey(task);
            if (key !== this.selectedTask) {
                this.$store.commit('setTask', key);
            } else {
                this.$store.commit('setTask', null);
            }
        },

        open(action) {
            this.expanded = false;
            if (action.to) {
                this.$router.push(action.to);
            } else if (action.modal) {
                this.$store.commit('setModal', action.modal);
            }
        },

        hasTask(slug) {
            const tasks = this.project.tasks;
            if (!tasks) return false;
            if (Array.isArray(slug)) {
                for (const s of slug) {
                    if (tasks.includes(s)) {
                        return true;
                    }
                }
                return false;
            } else {
                return tasks.includes(slug);
            }
        },

        hideChecklist() {
            this.$store.commit('hideChecklist', this.project.id);
        },

    },

}

</script>